import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connectToLocale } from '../Locale/Locale';
import { currencyFormat } from './currencyFormat';

/**
 * The currency component, uses the default render function defined below
 * unless a custom 'render' prop is specified.
 * @param {String} [className] //custom CSS class
 * @param {String} [currencyCode] //Example: USD
 * @param {boolean} [highlight] //determines whether to highlight currency display
 * @param {String} [locale] //Example: en_US
 * @param {boolean} [showVAT] //determines whether to show VAT text
 * @param {boolean} [strike] //determines whether to strike through currency display
 * @param {boolean} [tax] //indicates whether tax is included in value
 * @param {function} [render] //custom render function
 * @param {Number} value //price value
 * */
export const Currency = ({
  className,
  context,
  currencyCode,
  highlight,
  locale,
  render,
  showVAT,
  strike,
  tax,
  value,
  size,
}) => {
  const currency = currencyCode || (context && context.currencyCode);
  const localeString = locale || (context && context.locale) || 'en_US';

  // Show tax suffix if required.
  const displayVAT =
    showVAT !== undefined ? showVAT : (context && context.showVAT) || false;
  const taxIncluded = tax !== undefined ? tax : context && context.tax;
  const {
    value: formattedValue,
    prefix,
    suffix,
  } = currencyFormat(value, localeString, currency, taxIncluded);
  const classNameList = cn('c-currency', className, {
    'c-currency__highlight': highlight,
    'c-currency__strikethrough': strike,
    'c-currency--large': size === 'large',
  });

  // Render the component, using the provided renderer if present.
  const renderHandler =
    render && typeof render === 'function' ? render : defaultRender;
  return renderHandler({
    className: classNameList,
    prefix,
    value: formattedValue,
    suffix,
    displayVAT,
    size,
  });
};

/**
 * The default render prop, used if no custom renderer is provided.
 */
const defaultRender = ({ className, prefix, value, suffix, displayVAT }) => (
  <span className={className}>
    {prefix && <span className="c-currency__code">{prefix} </span>}
    <span className="c-currency__value">{value}</span>
    {displayVAT && suffix && <span className="c-currency__tax"> {suffix}</span>}
  </span>
);

Currency.propTypes = {
  className: PropTypes.string,
  context: PropTypes.shape({
    currencyCode: PropTypes.string,
    locale: PropTypes.string,
  }),
  currencyCode: PropTypes.string,
  highlight: PropTypes.bool,
  locale: PropTypes.string,
  render: PropTypes.func,
  showVAT: PropTypes.bool,
  size: PropTypes.oneOf(['', 'large']),
  strike: PropTypes.bool,
  tax: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

Currency.defaultProps = {
  className: '',
  currencyCode: undefined,
  highlight: false,
  locale: undefined,
  render: undefined,
  showVAT: undefined,
  size: '',
  strike: false,
  tax: undefined,
};

export default connectToLocale(Currency);
